import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #imagePriorityObserver = new MutationObserver(() => this.#prioritizeInitialProductImages());

  #currentURL;
  /** @type {Promise<void>} */
  #historyRender = Promise.resolve();
  #restoreRequestId = 0;
  connectedCallback() {
    super.connectedCallback();
    this.#prioritizeInitialProductImages();
    this.#imagePriorityObserver.observe(this, { childList: true, subtree: true });

    this.#captureCurrentURL();
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#captureCurrentURL);
    document.addEventListener(ThemeEvents.PaginatedListPageChange, this.#captureCurrentURL);
    window.addEventListener('popstate', this.#handlePopState);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('pagehide', this.#cancelProductGridScrollRestore);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#imagePriorityObserver.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#captureCurrentURL);
    document.removeEventListener(ThemeEvents.PaginatedListPageChange, this.#captureCurrentURL);
    window.removeEventListener('popstate', this.#handlePopState);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('pagehide', this.#cancelProductGridScrollRestore);
    this.#cancelProductGridScrollRestore();
  }

  #prioritizeInitialProductImages() {
    const items = this.querySelectorAll(
      '.product-grid > .product-grid__item[data-page="1"]:not(.product-grid__item--promotional-card)'
    );
    let prioritizedImageCount = 0;

    for (const item of items) {
      const image = item.querySelector('.card-gallery img.product-media__image');
      if (!(image instanceof HTMLImageElement)) continue;

      image.loading = 'eager';
      prioritizedImageCount += 1;

      if (prioritizedImageCount === 4) break;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Positions the product results below the visible header and facet controls.
   */
  scrollToResults() {
    const results = this.querySelector('#ResultsList');
    if (!results) return;

    const headerHeight = document.querySelector('header')?.getBoundingClientRect().height ?? 0;
    const stickyControlsHeight = [...document.querySelectorAll('.facets-controls-wrapper, .facets-toggle')]
      .filter((element) => element.getClientRects().length > 0)
      .reduce((height, element) => height + element.getBoundingClientRect().height, 0);
    const scrollTop = Math.max(
      0,
      window.scrollY + results.getBoundingClientRect().top - headerHeight - stickyControlsHeight
    );

    window.scrollTo({ top: scrollTop, behavior: 'instant' });
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #getCurrentURL() {
    return `${window.location.pathname}${window.location.search}`;
  }

  #captureCurrentURL = () => {
    this.#currentURL = this.#getCurrentURL();
  };

  #restoreProductGridScroll = (requestId) => {
    if (requestId !== this.#restoreRequestId) return;

    try {
      const cardAnchor = history.state?.productGridCardAnchor;
      const card = cardAnchor ? document.getElementById(cardAnchor) : null;

      if (card) {
        const cardOffset = history.state?.productGridCardOffset;
        card.scrollIntoView({ behavior: 'instant', block: 'start' });

        if (Number.isFinite(cardOffset)) {
          window.scrollBy({ top: -cardOffset, behavior: 'instant' });
        }
        return;
      }

      const scrollY = history.state?.productGridScrollY;
      if (Number.isFinite(scrollY)) {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      }
    } finally {
      if (requestId === this.#restoreRequestId) history.scrollRestoration = 'auto';
    }
  };

  #cancelProductGridScrollRestore = () => {
    this.#restoreRequestId += 1;
  };

  #waitForRestoreFrame(requestId) {
    return new Promise((resolve) => {
      requestAnimationFrame(() => resolve(requestId === this.#restoreRequestId));
    });
  }

  async #waitForStableLayout(requestId) {
    let previousCardTop;
    let previousDocumentHeight;

    for (let frame = 0; frame < 10; frame += 1) {
      if (!(await this.#waitForRestoreFrame(requestId))) return false;

      const cardAnchor = history.state?.productGridCardAnchor;
      const card = cardAnchor ? document.getElementById(cardAnchor) : null;
      const cardTop = card?.getBoundingClientRect().top;
      const documentHeight = document.documentElement.scrollHeight;

      if (cardTop === previousCardTop && documentHeight === previousDocumentHeight) return true;

      previousCardTop = cardTop;
      previousDocumentHeight = documentHeight;
    }

    return requestId === this.#restoreRequestId;
  }

  #queueProductGridScrollRestore = () => {
    this.#cancelProductGridScrollRestore();
    const requestId = this.#restoreRequestId;
    const historyRender = this.#historyRender;

    void (async () => {
      try {
        await historyRender;
        if (requestId !== this.#restoreRequestId || historyRender !== this.#historyRender) return;
        if (!(await this.#waitForStableLayout(requestId))) return;

        this.#restoreProductGridScroll(requestId);
      } catch (error) {
        if (requestId !== this.#restoreRequestId) return;

        history.scrollRestoration = 'auto';
        console.error('[ResultsList] Failed to restore product grid scroll', error);
      }
    })();
  };

  #handlePageShow = () => {
    this.#queueProductGridScrollRestore();
  };

  #handlePopState = () => {
    this.#historyRender = this.#restoreHistoryURL();
    this.#queueProductGridScrollRestore();
  };

  #restoreHistoryURL = async () => {
    const currentURL = this.#getCurrentURL();
    if (currentURL === this.#currentURL) return;
    this.#currentURL = currentURL;

    const facetsForm = document.querySelector('facets-form-component[data-primary-facets-form]');
    if (
      facetsForm instanceof HTMLElement &&
      'updateFiltersByURL' in facetsForm &&
      typeof facetsForm.updateFiltersByURL === 'function'
    ) {
      await facetsForm.updateFiltersByURL(window.location.href, {
        updateHistory: false,
        scroll: false,
        preservePage: true,
      });
      return;
    }

    await this.#renderCurrentURL();
  };

  #renderCurrentURL = async () => {
    const sectionIds = new Set([this.sectionId]);
    const facetsRoot = document.querySelector('[data-facets-section-id]');
    const facetsSectionId = facetsRoot instanceof HTMLElement ? facetsRoot.dataset.facetsSectionId : undefined;
    if (facetsSectionId) sectionIds.add(facetsSectionId);

    try {
      await Promise.all(
        [...sectionIds].map((sectionId) =>
          sectionRenderer.renderSection(sectionId, { url: new URL(window.location.href) })
        )
      );

      const resultsList = document.querySelector('results-list');
      if (resultsList && 'updatePaginationLinks' in resultsList) {
        resultsList.updatePaginationLinks();
      }
    } catch (error) {
      console.error('[ResultsList] Failed to restore history state', error);
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
